import { morph, MORPH_OPTIONS } from '@theme/morph';
import { CartRewards, DECLINED_ATTRIBUTE } from '@theme/psn-cart-rewards';

if (!customElements.get('psn-cart-drawer')) {
class PsnCartDrawer extends HTMLElement {
  static #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  static #SECTION_ID_PREFIX = 'psn-cart-drawer-';

  static #keyToSafe(key) {
    return String(key).replace(/:/g, '-');
  }
  constructor() {
    super();
    this.refs = {};
    this.state = {
      isOpen: false,
      isUpdating: false,
      openedOptimistically: false,
      previousScrollY: 0,
      // Tiers the customer asked to decline while another decline was in
      // flight. Tiers, not line keys: the key rotates when the section
      // re-renders, so it has to be looked up again at drain time.
      declineQueue: [],
      decliningQueue: false
    };
    this.shippingConfig = null;
    this.rewards = null;
    this.debouncedUpdate = this.#debounce(this.updateQuantity.bind(this), 300);
    this.debouncedNoteUpdate = this.#debounce(this.updateNote.bind(this), 200);
  }

  connectedCallback() {
    this.cacheRefs();
    this.bindEvents();
    this.initSessionStorage();
    this.initRewards();
    this.initUpsell();
    this.initUpsellCardSelects();
    this.updateStickyState();
    this.#initialCartSync();
    this.#installReploCartHook();
  }

  // Replo pages render their own React/Preact bundle that calls /cart/add.js
  // directly without dispatching cart events, so the drawer never opens.
  // We patch fetch only on Replo pages to bridge that gap by replaying the
  // event sequence the Alpine $store.cart.add already emits on native PDPs.
  #installReploCartHook() {
    if (window.__psnReploHookInstalled) return;
    const isReploPage = !!document.querySelector(
      '[data-replo-component-root], [data-replo-add-product-variant-to-cart], #replo-fullpage-element'
    );
    if (!isReploPage) return;
    window.__psnReploHookInstalled = true;

    const drawer = this;
    const origFetch = window.fetch.bind(window);
    window.fetch = async function (...args) {
      const response = await origFetch(...args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        const init = args[1] || (typeof args[0] === 'object' ? args[0] : null);
        const method = (init?.method || 'GET').toUpperCase();
        const isAdd = /\/cart\/add(\.js)?($|\?)/.test(url);
        if (!isAdd || method !== 'POST' || !response.ok) return response;

        // Do NOT bridge the drawer's OWN internal adds. Those methods
        // (swapStarterKitVariant / addToCartFromRecommendation / swapRewardVariant)
        // set state.isUpdating and refresh the drawer themselves; bridging here
        // would render the transient mid-swap state — e.g. the brief duplicate
        // line during swapStarterKitVariant's add-then-remove — on Replo pages
        // like the homepage. Replo's own React add fires while the drawer is
        // idle, so it still bridges.
        if (drawer.state?.isUpdating) return response;

        const sectionId = drawer.sectionId;
        const [cart, sectionsRes] = await Promise.all([
          origFetch('/cart.js').then((r) => r.json()),
          sectionId
            ? origFetch(`/?sections=${encodeURIComponent(sectionId)}`).then((r) => r.json())
            : Promise.resolve(null),
        ]);

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            cart,
            itemCount: cart.item_count,
            sections: sectionsRes || {},
            source: 'replo-hook',
          },
        }));
        document.dispatchEvent(new CustomEvent('cart:add', {
          bubbles: true,
          detail: { itemCount: cart.item_count },
        }));
        document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      } catch (err) {
        console.warn('[psn-cart] Replo cart-add hook failed', err);
      }
      return response;
    };
  }

  disconnectedCallback() {
    document.removeEventListener('cart:add', this.handleCartAdd); 
    document.removeEventListener('cart:update', this.handleCartUpdate);
    document.removeEventListener('cart:open', this.handleOpenCartDrawer);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('resize', this.handleResize);
  }

  cacheRefs() {
    this.refs.trigger = this.querySelector('[data-ref="trigger"]');
    this.refs.dialog = this.querySelector('[data-ref="dialog"]');
    this.refs.close = this.querySelector('[data-ref="close"]');
    this.refs.badge = this.querySelector('[data-ref="badge"]');
    this.refs.badgeText = this.querySelector('[data-ref="badgeText"]');
    this.refs.badgeCount = this.querySelector('[data-ref="badgeCount"]');
    this.refs.headerCount = this.querySelector('[data-ref="headerCount"]');
    this.refs.itemCount = this.querySelector('[data-ref="itemCount"]');
    this.refs.body = this.querySelector('[data-ref="body"]');
    this.refs.footer = this.querySelector('[data-ref="footer"]');
    this.refs.items = this.querySelector('[data-ref="items"]');
    this.refs.subtotal = this.querySelector('[data-ref="subtotal"]');
    this.refs.total = this.querySelector('[data-ref="total"]');
    this.refs.itemElements = this.querySelectorAll('[data-ref="items[]"]');
    this.refs.discountToggle = this.querySelector('[data-ref="discountToggle"]');
    this.refs.discountContent = this.querySelector('[data-ref="discountContent"]');
    this.refs.discountInput = this.querySelector('[data-ref="discountInput"]');
    this.refs.discountError = this.querySelector('[data-ref="discountError"]');
    this.refs.discountErrorText = this.querySelector('[data-ref="discountErrorText"]');
    this.refs.noteToggle = this.querySelector('[data-ref="noteToggle"]');
    this.refs.noteContent = this.querySelector('[data-ref="noteContent"]');
    this.refs.noteTextarea = this.querySelector('[data-ref="noteTextarea"]');
    
    this.refs.shipping = this.querySelector('[data-ref="shipping"]');
    this.refs.shippingSuccess = this.querySelector('[data-ref="shippingSuccess"]');
    this.refs.shippingLine = this.querySelector('[data-ref="shippingLine"]');
    this.refs.shippingFill = this.querySelector('[data-ref="shippingFill"]');
    this.refs.shippingMessageWrapper = this.querySelector('[data-ref="shippingMessageWrapper"]');
    this.refs.shippingMessage = this.querySelector('[data-ref="shippingMessage"]');
    this.refs.shippingCost = this.querySelector('[data-ref="shippingCost"]');
    this.refs.shippingFree = this.querySelector('[data-ref="shippingFree"]');
    this.refs.subscriptionButtons = this.querySelectorAll('[data-ref="subscriptionButton"]');
    this.refs.rewards = this.querySelector('[data-ref="rewards"]');
    this.refs.rewardsMessage = this.querySelector('[data-ref="rewardsMessage"]');
    this.refs.upsell = this.querySelector('[data-ref="upsell"]');
  }

  bindEvents() {
    this.refs.trigger?.addEventListener('click', () => this.open());
    this.refs.close?.addEventListener('click', () => this.close());
    this.refs.dialog?.addEventListener('click', (e) => this.handleBackdropClick(e));
    
    this.addEventListener('click', (e) => this.handleAction(e));
    this.addEventListener('input', (e) => {
      if (e.target.matches('[data-action="update-note"]')) {
        this.debouncedNoteUpdate(e);
      }
    });
    // Legacy subscription checkbox handler (kept for backward compat) +
    // delegated [data-action] dispatch so <select data-action="sk-size">
    // reaches handleAction the same way buttons do via click delegation.
    this.addEventListener('change', (e) => {
      if (e.target.matches('.psn-cart-item__subscription-checkbox')) {
        this.handleSubscriptionCheckbox(e);
      } else if (e.target.closest('[data-action]')) {
        this.handleAction(e);
      }
    });
    
    document.addEventListener('cart:add', this.handleCartAdd = (e) => this.onCartAdd(e));
    document.addEventListener('cart:update', this.handleCartUpdate = (e) => this.onCartUpdate(e));
    document.addEventListener('cart:open', this.handleOpenCartDrawer = (e) => this.open({ optimistic: e.detail?.optimistic }));
    document.addEventListener('keydown', (e) => this.handleEscape(e));
    window.addEventListener('pageshow', this.handlePageShow = (e) => this.onPageShow(e));
    window.addEventListener('resize', this.handleResize = this.#debounce(() => this.updateStickyState(), 100));
  }

  initSessionStorage() {
    const stored = sessionStorage.getItem('cart-count');
    if (!stored) return;

    try {
      const { value, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);
        if (count >= 0 && count !== this.getCurrentCount()) {
          this.updateBadge(count, false);
        }
      }
    } catch (error) {
      console.error('Failed to parse cart count from session storage:', error);
    }
  }

  // `optimistic` opens the drawer before the cart mutation has returned, with
  // .psn-cart--busy standing in for the not-yet-rendered contents. Cleared by
  // onCartUpdate once the section refresh lands, or by cancelOptimisticOpen()
  // if the add fails. See PS001R-909.
  open({ optimistic = false } = {}) {
    if (this.state.isOpen) return;

    this.state.isOpen = true;
    this.state.openedOptimistically = optimistic;
    this.state.previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.state.previousScrollY}px`;

      this.refs.dialog.showModal();
      this.refs.dialog.classList.add('psn-cart--open');
      if (optimistic) {
        // On a cart that already has lines the existing rows are still correct,
        // so dim them. With nothing to show, a skeleton stands in — opening
        // onto 'your basket is empty' would read worse than waiting.
        // Checked against the DOM, not .psn-cart--empty: morph runs with
        // childrenOnly so that class never gets cleared off the dialog.
        const hasLines = this.querySelector('.psn-cart-item') !== null;
        this.refs.dialog.classList.add(
          hasLines ? 'psn-cart--busy' : 'psn-cart--busy-empty'
        );
      }

      this.updateStickyState();

      // Swiper initialised while the drawer was hidden measures 0-width slides.
      // Always refresh on open: update() if instance exists (fixes dimensions),
      // full init if it doesn't (race condition — Swiper loaded after connectedCallback).
      if (this._upsellSwiper) {
        this._upsellSwiper.update();
      } else {
        this.initUpsell();
        this.initUpsellCardSelects();
      }

      this.dispatchEvent(new CustomEvent('psn-cart:open', { bubbles: true }));
    });
  }

  // Drops the optimistic loading state without touching open/closed state.
  // Called unconditionally once an add settles: onCartUpdate only clears it
  // when a section render came back, and /cart/add.js can answer 200 with no
  // sections. Without this the busy class survives, and its pointer-events:none
  // covers the footer, so the checkout button stays dead.
  clearOptimisticState() {
    this.state.openedOptimistically = false;
    this.refs.dialog?.classList.remove('psn-cart--busy', 'psn-cart--busy-empty');
  }

  // Rolls back an optimistic open when the add never lands (422, 5xx, network).
  // Only closes a drawer this component opened on the customer's behalf — one
  // they opened deliberately stays put.
  cancelOptimisticOpen() {
    if (!this.state.openedOptimistically) return;
    this.close();
  }

  close() {
    if (!this.state.isOpen) return;

    this.state.isOpen = false;
    this.state.openedOptimistically = false;
    this.refs.dialog.classList.remove('psn-cart--busy', 'psn-cart--busy-empty');
    this.refs.dialog.classList.remove('psn-cart--open');
    this.refs.dialog.classList.add('psn-cart--closing');

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.state.previousScrollY, behavior: 'instant' });

      this.refs.dialog.close();
      this.refs.dialog.classList.remove('psn-cart--closing');

      this.dispatchEvent(new CustomEvent('psn-cart:close', { bubbles: true }));
    };

    this.#onAnimationEnd(this.refs.dialog, finish, { subtree: false });
    setTimeout(finish, 400);
  }

  handleBackdropClick(e) {
    if (e.target === this.refs.dialog) {
      this.close();
    }
  }

  handleEscape(e) {
    if (e.key === 'Escape' && this.state.isOpen) {
      e.preventDefault();
      this.close();
    }
  }

  // Prefer detail.cart.item_count (true cart total) when the emitter supplies
  // a cart object; fall back to detail.itemCount otherwise. No fetch fallback —
  // refreshCart() updates the badge directly from the cart response, and
  // emitters that don't pass a cart object are expected to have already
  // computed the correct total upstream.
  #resolveItemCount(detail) {
    if (detail?.cart && typeof detail.cart.item_count === 'number') {
      return detail.cart.item_count;
    }
    if (typeof detail?.itemCount === 'number') {
      return detail.itemCount;
    }
    return 0;
  }

  onCartAdd(e) {
    if (this.hasAttribute('auto-open')) {
      this.open();
    }
    this.updateBadge(this.#resolveItemCount(e.detail), true);
  }

  onCartUpdate(e) {
    // Skip our own dispatches — refreshCart/applyCartAfterRewards already
    // morphed the DOM, so re-processing would cause a double update.
    if (e.detail?.source === 'psn-cart-drawer') return;

    if (e.detail?.sections?.[this.sectionId]) {
      const sectionHtml = e.detail.sections[this.sectionId];
      // Emitters that already hold a cart object pass it through; the rest let
      // us read it off the section render instead of fetching /cart.js again.
      const cart = e.detail.cart ?? PsnCartDrawer.#parseCartState(sectionHtml);
      this.#updateCartSection(sectionHtml);
      requestAnimationFrame(() => {
        this.cacheRefs();
        // Real contents are in — drop the optimistic loading state.
        this.state.openedOptimistically = false;
        this.refs.dialog?.classList.remove('psn-cart--busy', 'psn-cart--busy-empty');
        this.updateStickyState();
        this.initUpsell();
        this.initUpsellCardSelects();
        this.initRewards();
        if (cart && this.rewards) {
          this.rewards.sync(cart);
        }
        if (this.refs.shipping) {
          this.initShippingProgress();
          if (cart && this.shippingConfig) {
            this.calculateShipping(cart);
          }
        }
      });
      this.updateBadge(this.#resolveItemCount({ ...e.detail, cart }), false);
      return;
    }

    this.updateBadge(this.#resolveItemCount(e.detail), false);
  }
 
  onPageShow(e) {
    if (e.persisted) {
      this.initSessionStorage();
    }
  }

  async handleAction(e) {
    const button = e.target.closest('[data-action]');
    if (!button) return;

    const action = button.dataset.action;
    
    if (action === 'toggle-discount') {
      this.toggleDiscount();
    } else if (action === 'toggle-note') {
      this.toggleNote();
    } else if (action === 'toggle-subscription') {
      const key = button.dataset.key;
      this.toggleSubscription(key);
    } else if (action === 'apply-discount') {
      await this.applyDiscount();
    } else if (action === 'remove-discount') {
      await this.removeDiscount(button.dataset.code);
    } else if (action === 'update-note') {
      this.debouncedNoteUpdate(e);
    } else if (action === 'upgrade-subscription') {
      const key = button.dataset.key;
      const sellingPlanId = parseInt(button.dataset.sellingPlanId, 10);
      const quantity = parseInt(button.dataset.quantity, 10);
      await this.upgradeToSubscription(key, sellingPlanId, quantity);
    } else if (action === 'downgrade-subscription') {
      const key = button.dataset.key;
      const quantity = parseInt(button.dataset.quantity, 10);
      await this.downgradeFromSubscription(key, quantity);
    } else if (action === 'sk-upgrade') {
      const key = button.dataset.key;
      const newVariantId = parseInt(button.dataset.variant, 10);
      const sellingPlanId = parseInt(button.dataset.plan, 10);
      const quantity = parseInt(button.dataset.quantity, 10);
      const sourceHandle = button.dataset.source;
      await this.swapStarterKitVariant(key, newVariantId, sellingPlanId, quantity, sourceHandle);
    } else if (action === 'sk-size') {
      const select = button;
      const key = select.dataset.key;
      const sellingPlanId = parseInt(select.dataset.plan, 10);
      const quantity = parseInt(select.dataset.quantity, 10);
      const sourceHandle = select.dataset.source;
      const currentVariantId = parseInt(select.dataset.currentVariant, 10);
      const newVariantId = parseInt(select.value, 10);
      if (!newVariantId || newVariantId === currentVariantId) return;
      const ok = await this.swapStarterKitVariant(key, newVariantId, sellingPlanId, quantity, sourceHandle);
      if (!ok) select.value = String(currentVariantId);
    } else if (action === 'reward-size') {
      const select = button;
      const oldKey = select.dataset.key;
      const tierId = select.dataset.tier;
      const currentVariantId = parseInt(select.dataset.currentVariant, 10);
      const newVariantId = parseInt(select.value, 10);
      if (!newVariantId || newVariantId === currentVariantId) return;
      const ok = await this.swapRewardVariant(oldKey, newVariantId, tierId);
      if (!ok) select.value = String(currentVariantId);
    } else if (action === 'decline-reward') {
      await this.declineRewardGift(button.dataset.key, button.dataset.tier);
    } else if (action === 'add-to-cart') {
      const variantId = button.dataset.variantId;
      if (variantId) {
        await this.addToCartFromRecommendation(variantId, button);
      }
    } else {
      if (this.state.isUpdating) return;

      const key = button.dataset.key;

      if (action === 'increase') {
        this.debouncedUpdate(key, 1);
      } else if (action === 'decrease') {
        this.debouncedUpdate(key, -1);
      } else if (action === 'remove') {
        await this.removeItem(key);
      }
    }
  }

  async updateQuantity(key, change) {
    if (this.state.isUpdating) return;

    const input = this.querySelector(`[data-key="${key}"].psn-cart-item__qty-input`);
    if (!input) return;

    const currentQty = parseInt(input.value, 10);
    const newQty = Math.max(0, currentQty + change);

    if (newQty === 0) {
      return this.removeItem(key);
    }

    this.state.isUpdating = true;
    this.showShimmer(key);
    this.hideError(key);

    const sections = this.getAllCartSections();
    const body = JSON.stringify({
      id: key,
      quantity: newQty,
      sections: sections.join(','),
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(routes.cart_change_url, this.#fetchConfig('json', { body }));
      const data = await response.json();

      if (data.errors) {
        this.showError(key, data.errors);
        input.value = currentQty;
      } else {
        await this.refreshCart(data);
        this.syncQuantitySelectors(data);
      }
    } catch (error) {
      console.error('Cart update failed:', error);
      this.showError(key, 'An error occurred. Please try again.');
      input.value = currentQty;
    } finally {
      this.state.isUpdating = false;
      this.hideShimmer(key);
    }
  }

  async removeItem(key) {
    if (this.state.isUpdating) return;

    // Lock after the lookup, not before: the early return below used to leave
    // isUpdating stuck on, which silently no-oped every later mutation for the
    // life of the page.
    const itemElement = this.querySelector(`.psn-cart-item[data-key="${key}"]`);
    if (!itemElement) return;

    this.state.isUpdating = true;

    const itemsToRemove = [
      itemElement,
      ...Array.from(this.refs.itemElements).filter(
        row => row.dataset.parentKey === itemElement.dataset.key
      )
    ];

    // Hide trailing UI rendered between this cart item and the next cart item
    // (native upgrade block, kit upgrade block, kit size selector). The
    // collapse animation on the cart item itself doesn't include those
    // siblings, so without explicit hiding they linger until /cart/change.js
    // returns and the section morphs in.
    let trailing = itemElement.nextElementSibling;
    while (trailing && !trailing.classList.contains('psn-cart-item')) {
      if (trailing.classList.contains('psn-cart-item__upgrade-block')) {
        trailing.classList.add('psn-cart-item__upgrade-block--removing');
      } else if (trailing.classList.contains('psn-cart-item__sk-size')) {
        trailing.classList.add('psn-cart-item__sk-size--removing');
      }
      trailing = trailing.nextElementSibling;
    }

    // If the first cart item is being removed, pre-apply margin-top: 0 to the next
    // cart item so there is no jump when :first-of-type transfers after removal
    if (itemElement.matches(':first-of-type')) {
      let nextItem = itemElement.nextElementSibling;
      while (nextItem && !nextItem.classList.contains('psn-cart-item')) {
        nextItem = nextItem.nextElementSibling;
      }
      if (nextItem) nextItem.style.marginTop = '0';
    }

    itemsToRemove.forEach(item => {
      if (this.#prefersReducedMotion()) {
        item.remove();
      } else {
        item.style.setProperty('--row-height', `${item.clientHeight}px`);
        item.classList.add('psn-cart-item--removing');
        this.#onAnimationEnd(item, () => item.remove());
      }
    });

    const sections = this.getAllCartSections();
    const body = JSON.stringify({
      id: key,
      quantity: 0,
      sections: sections.join(','),
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(routes.cart_change_url, this.#fetchConfig('json', { body }));
      const data = await response.json();

      if (!data.errors) {
        await this.refreshCart(data);
        this.syncQuantitySelectors(data);
      }
    } catch (error) {
      console.error('Cart remove failed:', error);
    } finally {
      this.state.isUpdating = false;
    }
  }

  async refreshCart(cart) {
    const newItemCount = cart.item_count || 0;

    if (cart.sections && cart.sections[this.sectionId]) {
      this.#updateCartSection(cart.sections[this.sectionId]);

      await new Promise(resolve => requestAnimationFrame(resolve));

      this.cacheRefs();
      this.updateStickyState();
      this.initUpsell();
      this.initUpsellCardSelects();

      this.initRewards();

      if (this.refs.shipping && cart) {
        this.initShippingProgress();
        if (this.shippingConfig) {
          this.calculateShipping(cart);
        }
      }
    }

    this.updateBadge(newItemCount, false);

    await this.#clearDeclinedGiftsIfEmpty(cart);

    if (this.rewards) {
      this.rewards.sync(cart);
    }

    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        cart,
        itemCount: newItemCount,
        sections: cart.sections,
        source: 'psn-cart-drawer'
      }
    }));
  }

  /**
   * Called by CartRewards after it has added or removed gift lines.
   * Fetches a fresh cart + sections, morphs DOM, and re-renders rewards UI
   * WITHOUT triggering another reconciliation — prevents mutation loops.
   */
  async applyCartAfterRewards(prefetched = null) {
    try {
      // The reconciler's own last call already asked for sections, so in the
      // common case there is nothing left to fetch. Falls back to the network
      // whenever that response is missing or unusable.
      let freshCart = this.#cartFromMutationResponse(prefetched);

      if (!freshCart) {
        const sections = this.getAllCartSections();
        // POST /cart/update.js with no updates returns the full cart object
        // PLUS the requested sections — one round-trip instead of two.
        const response = await fetch(routes.cart_update_url, this.#fetchConfig('json', {
          body: JSON.stringify({
            sections: sections.join(','),
            sections_url: window.location.pathname
          })
        }));
        freshCart = await response.json();
      }

      if (freshCart.sections && freshCart.sections[this.sectionId]) {
        this.#updateCartSection(freshCart.sections[this.sectionId]);
        await new Promise(resolve => requestAnimationFrame(resolve));
        this.cacheRefs();
        this.updateStickyState();
        this.initUpsell();
        this.initUpsellCardSelects();
        this.initRewards();
        this.rewards?.renderOnly(freshCart);
      }

      this.updateBadge(freshCart.item_count || 0, false);

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          cart: freshCart,
          itemCount: freshCart.item_count || 0,
          sections: freshCart.sections,
          source: 'psn-cart-drawer'
        }
      }));
    } catch (error) {
      console.error('PSN Cart: applyCartAfterRewards failed:', error);
    }
  }

  syncQuantitySelectors(cart) {
    if (!cart.items) return;

    cart.items.forEach(item => {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`
      );

      selectors.forEach(selector => {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (input) {
          input.setAttribute('data-cart-quantity', item.quantity.toString());
          if (typeof selector.updateCartQuantity === 'function') {
            selector.updateCartQuantity();
          }
        }
      });
    });
  }

  showShimmer(key) {
    const safe = PsnCartDrawer.#keyToSafe(key);
    const shimmer = this.querySelector(`[data-ref="shimmer-${safe}"]`);
    if (shimmer) {
      shimmer.classList.add('psn-cart-item__shimmer--active');
    }
  }

  hideShimmer(key) {
    const safe = PsnCartDrawer.#keyToSafe(key);
    const shimmer = this.querySelector(`[data-ref="shimmer-${safe}"]`);
    if (shimmer) {
      shimmer.classList.remove('psn-cart-item__shimmer--active');
    }
  }

  showError(key, message) {
    const safe = PsnCartDrawer.#keyToSafe(key);
    const errorContainer = this.querySelector(`[data-ref="error-${safe}"]`);
    const errorText = this.querySelector(`[data-ref="errorText-${safe}"]`);

    if (errorContainer && errorText) {
      errorText.textContent = message;
      errorContainer.classList.remove('psn-cart-item__error--hidden');
    }
  }

  hideError(key) {
    const safe = PsnCartDrawer.#keyToSafe(key);
    const errorContainer = this.querySelector(`[data-ref="error-${safe}"]`);
    if (errorContainer) {
      errorContainer.classList.add('psn-cart-item__error--hidden');
    }
  }

  updateBadge(count, animate = false) {
    // Drawer-internal badge refs (data-ref="badge*") are legacy — the current
    // markup does not render them. Guard every use locally so the header
    // bubble update + sessionStorage sync below are never skipped because of
    // their absence.
    if (this.refs.badgeCount) {
      this.refs.badgeCount.textContent = count;
    }
    if (this.refs.badge) {
      this.refs.badge.classList.toggle('psn-cart-badge--hidden', count === 0);
    }
    if (this.refs.headerCount) {
      this.refs.headerCount.textContent = `${count}`;
    }

    this.#updateHeaderCartCount(count);

    sessionStorage.setItem('cart-count', JSON.stringify({
      value: count,
      timestamp: Date.now()
    }));

    if (animate && count > 0 && this.refs.badge && this.refs.badgeText && !this.#prefersReducedMotion()) {
      this.animateBadge();
    }
  }

  // Single source of truth for the header cart-count bubble.
  // Never replace `.header__cart-icon` itself — Alpine `:class` reactivity
  // (showMegaMenu / backgroundIsTransparent) lives on that node. We only
  // mutate `.cart-count-bubble` inside it.
  #updateHeaderCartCount(count) {
    const cartIconBubble = document.getElementById('cart-icon-bubble');
    if (!cartIconBubble) return;

    const headerCartIcon = cartIconBubble.querySelector('.header__cart-icon');
    if (!headerCartIcon) return;

    let bubble = headerCartIcon.querySelector('.cart-count-bubble');

    if (count === 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML =
        `<span aria-hidden="true">${count < 100 ? `(${count})` : ''}</span>` +
        `<span class="visually-hidden">${count} items</span>`;
      headerCartIcon.appendChild(bubble);
      return;
    }

    const visible = bubble.querySelector('span[aria-hidden="true"]');
    const sr = bubble.querySelector('.visually-hidden');
    if (visible) visible.textContent = count < 100 ? `(${count})` : '';
    if (sr) sr.textContent = `${count} items`;
  }

  async animateBadge() {
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    this.refs.badge.classList.add('psn-cart-badge--animating');
    
    await this.#onAnimationEnd(this.refs.badgeText);
    
    this.refs.badge.classList.remove('psn-cart-badge--animating');
  }

  updateStickyState() {
    if (!this.refs.dialog || !this.state.isOpen) return;

    const content = this.refs.body;
    const footer = this.refs.footer;

    if (!content || !footer) {
      this.refs.dialog.setAttribute('data-sticky', 'false');
      return;
    }

    const dialogHeight = this.refs.dialog.getBoundingClientRect().height;
    const footerHeight = footer.getBoundingClientRect().height;
    const ratio = footerHeight / dialogHeight;

    this.refs.dialog.setAttribute('data-sticky', ratio > 0.5 ? 'false' : 'true');
  }

  async #initialCartSync() {
    // Runs before the rewards guard on purpose. The rewards block is inside
    // the non-empty branch of the section, so on an empty cart this.rewards is
    // null and CartRewards never gets to reset anything. Reading the embedded
    // cart state costs no request.
    await this.#clearDeclinedGiftsIfEmpty(this.#readEmbeddedCart());

    if (!this.rewards) return;
    try {
      const resp = await fetch(`${routes.cart_url}.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const cart = await resp.json();
      this.rewards.sync(cart);
    } catch (err) {
      console.error('PSN Cart: initial rewards sync failed:', err);
    }
  }

  // Section IDs requested via the Section Rendering API. Each entry is the
  // canonical `{{ section.id }}` value emitted by Shopify into the matching
  // custom element's data-section-id attribute — for the drawer that's the
  // psn-cart-drawer section inside overlay-group; for the /cart page it's
  // the cart-items-component section. Header is never grabbed because its
  // markup contains neither element; the header cart bubble is updated
  // directly by #updateHeaderCartCount(count).
  // Section params for a cart mutation. Exposed for CartRewards, which owns no
  // DOM of its own and must not reach for window.
  cartSectionsParam() {
    return {
      sections: this.getAllCartSections().join(','),
      sections_url: window.location.pathname
    };
  }

  getAllCartSections() {
    const sections = new Set([this.sectionId]);

    document.querySelectorAll('cart-items-component, psn-cart-drawer').forEach(component => {
      const sectionId = component.dataset.sectionId;
      if (sectionId) {
        sections.add(sectionId);
      }
    });

    return Array.from(sections);
  }

  getCurrentCount() {
    if (!this.refs.itemCount) return 0;
    return parseInt(this.refs.itemCount.textContent || '0', 10);
  }

  get sectionId() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      throw new Error('PSN Cart Drawer: Section ID is missing');
    }
    return sectionId;
  }

  toggleDiscount() {
    const toggle = this.refs.discountToggle;
    const content = this.refs.discountContent;
    if (!toggle || !content) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;

    toggle.setAttribute('aria-expanded', newState.toString());
    content.classList.toggle('psn-cart__extras-content--hidden', !newState);
    
    if (newState) {
      content.removeAttribute('inert');
    } else {
      content.setAttribute('inert', '');
    }
  }

  toggleNote() {
    const toggle = this.refs.noteToggle;
    const content = this.refs.noteContent;
    if (!toggle || !content) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;

    toggle.setAttribute('aria-expanded', newState.toString());
    content.classList.toggle('psn-cart__extras-content--hidden', !newState);
    
    if (newState) {
      content.removeAttribute('inert');
    } else {
      content.setAttribute('inert', '');
    }
  }

  toggleSubscription(key) {
    const safe = PsnCartDrawer.#keyToSafe(key);
    const toggle = this.querySelector(`[data-ref="subscriptionToggle-${safe}"]`);
    const content = this.querySelector(`[data-ref="subscriptionContent-${safe}"]`);
    if (!toggle || !content) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;

    toggle.setAttribute('aria-expanded', newState.toString());
    content.classList.toggle('psn-cart-item__subscription-content--hidden', !newState);
    
    if (newState) {
      content.removeAttribute('inert');
    } else {
      content.setAttribute('inert', '');
    }
  }

  async handleSubscriptionCheckbox(e) {
    const checkbox = e.target;
    if (!checkbox.checked) {
      checkbox.checked = false;
      return;
    }

    const key = checkbox.dataset.key;
    const sellingPlanId = parseInt(checkbox.dataset.sellingPlanId, 10);
    const quantity = parseInt(checkbox.dataset.quantity, 10);

    await this.upgradeToSubscription(key, sellingPlanId, quantity);
  }

  async applyDiscount() {
    const input = this.refs.discountInput;
    if (!input) return;

    const code = input.value.trim();
    if (!code) return;

    const existingCodes = this.#getExistingDiscounts();
    if (existingCodes.includes(code)) {
      input.value = '';
      return;
    }

    this.#hideDiscountError();

    try {
      const response = await fetch(routes.cart_update_url, this.#fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingCodes, code].join(','),
          sections: this.getAllCartSections().join(','),
          sections_url: window.location.pathname
        })
      }));

      const data = await response.json();

      const discount = data.discount_codes?.find(d => d.code === code);
      
      if (!discount || !discount.applicable) {
        const isShippingDiscount = this.#checkShippingDiscount(data, code, existingCodes);
        
        if (isShippingDiscount) {
          this.#showDiscountError('Shipping discount cannot be applied to cart');
        } else {
          this.#showDiscountError('Discount code cannot be applied to your cart');
        }
        input.value = '';
        return;
      }

      await this.refreshCart(data);
      input.value = '';
    } catch (error) {
      console.error('Discount apply failed:', error);
      this.#showDiscountError('An error occurred. Please try again.');
    }
  }

  async removeDiscount(code) {
    if (!code) return;

    const existingCodes = this.#getExistingDiscounts();
    const filteredCodes = existingCodes.filter(c => c !== code);

    try {
      const response = await fetch(routes.cart_update_url, this.#fetchConfig('json', {
        body: JSON.stringify({
          discount: filteredCodes.join(','),
          sections: this.getAllCartSections().join(','),
          sections_url: window.location.pathname
        })
      }));

      const data = await response.json();
      await this.refreshCart(data);
    } catch (error) {
      console.error('Discount remove failed:', error);
    }
  }

  async updateNote(e) {
    const textarea = e.target;
    if (!textarea) return;

    const note = textarea.value;

    try {
      await fetch(routes.cart_update_url, this.#fetchConfig('json', {
        body: JSON.stringify({ note })
      }));
    } catch (error) {
      console.error('Note update failed:', error);
    }
  }

  async upgradeToSubscription(key, sellingPlanId, quantity) {
    if (this.state.isUpdating) return;
    if (!key || !sellingPlanId || !quantity) {
      console.error('Invalid parameters for subscription upgrade');
      return;
    }

    this.state.isUpdating = true;
    this.showShimmer(key);
    this.hideError(key);

    const sections = this.getAllCartSections();
    const body = JSON.stringify({
      id: key,
      quantity,
      selling_plan: sellingPlanId,
      sections: sections.join(','),
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(routes.cart_change_url, this.#fetchConfig('json', { body }));
      const data = await response.json();

      if (data.errors || data.status === 'bad_request') {
        this.showError(key, data.description || data.errors || 'Failed to upgrade to subscription');
      } else {
        await this.refreshCart(data);
        this.syncQuantitySelectors(data);
      }
    } catch (error) {
      console.error('Subscription upgrade failed:', error);
      this.showError(key, 'An error occurred. Please try again.');
    } finally {
      this.state.isUpdating = false;
      this.hideShimmer(key);
    }
  }

  async downgradeFromSubscription(key, quantity) {
    if (this.state.isUpdating) return;
    if (!key || !quantity) {
      console.error('Invalid parameters for subscription downgrade');
      return;
    }

    this.state.isUpdating = true;
    this.showShimmer(key);
    this.hideError(key);

    const sections = this.getAllCartSections();
    const body = JSON.stringify({
      id: key,
      quantity,
      selling_plan: null,
      sections: sections.join(','),
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(routes.cart_change_url, this.#fetchConfig('json', { body }));
      const data = await response.json();

      if (data.errors || data.status === 'bad_request') {
        this.showError(key, data.description || data.errors || 'Failed to remove subscription');
      } else {
        await this.refreshCart(data);
        this.syncQuantitySelectors(data);
      }
    } catch (error) {
      console.error('Subscription downgrade failed:', error);
      this.showError(key, 'An error occurred. Please try again.');
    } finally {
      this.state.isUpdating = false;
      this.hideShimmer(key);
    }
  }

  // Swap an OTP anchor line for a Starter Kit subscription variant (or swap
  // between size variants on an already-upgraded line). Add-then-remove order
  // keeps the cart non-empty for the duration. Returns true on success so
  // <select> callers know whether to revert their value on failure.
  async swapStarterKitVariant(oldKey, newVariantId, sellingPlanId, quantity, sourceHandle) {
    if (this.state.isUpdating) return false;
    if (!oldKey || !newVariantId || !sellingPlanId || !quantity || !sourceHandle) {
      console.error('Invalid parameters for starter kit swap');
      return false;
    }

    this.state.isUpdating = true;
    this.showShimmer(oldKey);
    this.hideError(oldKey);

    const sections = this.getAllCartSections().join(',');
    const sectionsUrl = window.location.pathname;

    try {
      // Propagate _source onto the new subscription line. Defensive: if cart-
      // transform / free-gift logic keys off _source, gifts persist after swap.
      const addRes = await fetch(routes.cart_add_url, this.#fetchConfig('json', {
        body: JSON.stringify({
          items: [{
            id: newVariantId,
            quantity,
            selling_plan: sellingPlanId,
            properties: { _source: sourceHandle }
          }],
          sections,
          sections_url: sectionsUrl
        })
      }));
      const addData = await addRes.json();
      if (!addRes.ok || addData.status === 'bad_request' || addData.errors) {
        this.showError(oldKey, addData.description || addData.errors || 'Could not upgrade kit');
        return false;
      }

      const removeRes = await fetch(routes.cart_change_url, this.#fetchConfig('json', {
        body: JSON.stringify({
          id: oldKey,
          quantity: 0,
          sections,
          sections_url: sectionsUrl
        })
      }));
      const removeData = await removeRes.json();
      if (!removeRes.ok || removeData.errors) {
        // Add succeeded but remove failed — cart now contains both lines.
        // Surface the error; customer can remove the old line manually.
        this.showError(oldKey, removeData.description || removeData.errors || 'Could not remove old item');
        return false;
      }

      await this.refreshCart(removeData);
      this.syncQuantitySelectors(removeData);
      return true;
    } catch (error) {
      console.error('Starter kit swap failed:', error);
      this.showError(oldKey, 'An error occurred. Please try again.');
      return false;
    } finally {
      this.state.isUpdating = false;
      this.hideShimmer(oldKey);
    }
  }

  // Swap a reward-gift line to a different variant of the same product while
  // preserving the _reward_gift_tier property so the tiered-rewards Function
  // still validates the line and applies the 100% discount at checkout.
  //
  // Order: REMOVE → ADD (with reconcile suppression). Doing it the other way
  // briefly leaves two lines for the same tier in the cart, and any cart event
  // that fires mid-swap would either dispatch /cart.js re-fetches or trigger
  // rewards.sync, both of which can spawn duplicate gift lines.
  async swapRewardVariant(oldKey, newVariantId, tierId) {
    if (this.state.isUpdating) return false;
    if (!oldKey || !newVariantId || !tierId) {
      console.error('Invalid parameters for reward variant swap');
      return false;
    }

    this.state.isUpdating = true;
    this.showShimmer(oldKey);
    this.hideError(oldKey);

    // Tell CartRewards to ignore intermediate states during this swap.
    if (this.rewards?.state) this.rewards.state.suppressReconcile = true;

    const sections = this.getAllCartSections().join(',');
    const sectionsUrl = window.location.pathname;

    try {
      // 2 round-trips. REMOVE returns the full cart object (no sections — we
      // only need item_count/items). ADD with `sections` returns the rendered
      // section HTML that already reflects both the remove AND the add (since
      // it runs second). Combine the two for a single refreshCart() call —
      // no extra GET /cart.js needed.
      const removeRes = await fetch(routes.cart_change_url, this.#fetchConfig('json', {
        body: JSON.stringify({ id: oldKey, quantity: 0 })
      }));
      const removeData = await removeRes.json();
      if (!removeRes.ok || removeData.errors) {
        console.error('[psn-cart] reward swap remove failed', removeData);
        return false;
      }

      const addRes = await fetch(routes.cart_add_url, this.#fetchConfig('json', {
        body: JSON.stringify({
          items: [{
            id: newVariantId,
            quantity: 1,
            properties: { _reward_gift_tier: tierId }
          }],
          sections,
          sections_url: sectionsUrl
        })
      }));
      const addData = await addRes.json();
      if (!addRes.ok || addData.status === 'bad_request' || addData.errors) {
        console.error('[psn-cart] reward swap add failed', addData);
        return false;
      }

      const addedItem = Array.isArray(addData.items) ? addData.items[0] : addData;
      const composedCart = {
        ...removeData,
        items: [...(removeData.items || []), addedItem],
        item_count: (removeData.item_count || 0) + (addedItem?.quantity || 1),
        sections: addData.sections,
      };

      await this.refreshCart(composedCart);
      this.syncQuantitySelectors(composedCart);
      return true;
    } catch (error) {
      console.error('Reward variant swap failed:', error);
      return false;
    } finally {
      this.state.isUpdating = false;
      this.hideShimmer(oldKey);
      if (this.rewards?.state) this.rewards.state.suppressReconcile = false;
    }
  }

  // Remove a tiered reward gift the customer does not want and record the
  // refusal on the cart, so the reconciler stops putting it back.
  //
  // Two round trips, attribute first. That order makes the second call
  // optional: if the line key has gone stale the change call fails loudly, the
  // refusal is already stored, and the reconciler clears the line through its
  // existing "tier no longer qualifies" branch.
  //
  // Not one /cart/update.js carrying `updates` and `attributes` together,
  // which would halve the latency. update.js ADDS a line when the key in
  // `updates` matches nothing, and Shopify rotates a line key whenever its
  // properties or discount applications change, which is exactly what the
  // 100% Function discount on this line is. change.js never adds; on an
  // unknown id it returns 400. A loud failure beats a second free gift.
  async declineRewardGift(key, tierId) {
    if (!key || !tierId) {
      console.error('Invalid parameters for reward gift decline');
      return false;
    }

    // This branch of handleAction sits above the shared guard, so it carries
    // its own, the same way swapRewardVariant does. Unlike swapRewardVariant
    // it queues instead of dropping: a cart can hold two gifts, and swallowing
    // the second bin click is exactly the "I cannot remove it" the ticket is
    // about.
    if (this.state.isUpdating) {
      if (!this.state.declineQueue.includes(tierId)) {
        this.state.declineQueue.push(tierId);
        // Shimmer so the click visibly registers. Morph clears it when the
        // in-flight decline lands, and the drain collapses the card a frame
        // later.
        const waiting = this.querySelector(`.psn-cart__reward-gift[data-reward-tier="${tierId}"]`);
        if (waiting?.dataset.key) this.showShimmer(waiting.dataset.key);
      }
      return false;
    }

    const rewards = this.rewards;
    if (!rewards?.state) return false;

    this.state.isUpdating = true;

    // Record the intent before the network. A sync landing mid-flight reads a
    // cart that predates the write, and pendingDeclines is what stops it
    // rebuilding declinedTiers without this tier.
    rewards.state.pendingDeclines.add(tierId);
    rewards.state.declinedTiers.add(tierId);
    rewards.state.suppressReconcile = true;

    this.#collapseRewardGift(key);

    const sections = this.getAllCartSections().join(',');
    const sectionsUrl = window.location.pathname;

    try {
      const attrRes = await fetch(routes.cart_update_url, this.#fetchConfig('json', {
        body: JSON.stringify({ attributes: rewards.declinedAttributePayload() })
      }));

      if (!attrRes.ok) {
        console.error('[psn-cart] decline attribute write failed');
        this.#revertDecline(rewards, tierId);
        await this.applyCartAfterRewards();
        return false;
      }

      const removeRes = await fetch(routes.cart_change_url, this.#fetchConfig('json', {
        body: JSON.stringify({ id: key, quantity: 0, sections, sections_url: sectionsUrl })
      }));
      const removeData = await removeRes.json();

      if (!removeRes.ok || removeData.errors) {
        // Stale key, most likely. The refusal is already stored, so hand the
        // removal to the reconciler rather than leave a card the customer has
        // dismissed sitting in the drawer.
        console.warn('[psn-cart] decline remove failed, handing over to reconcile', removeData);
        rewards.state.suppressReconcile = false;
        const fresh = await fetch(`${routes.cart_url}.js`, {
          headers: { Accept: 'application/json' }
        }).then((r) => r.json());
        await rewards.sync(fresh);
        return true;
      }

      await this.refreshCart(removeData);
      this.syncQuantitySelectors(removeData);

      // refreshCart's sync ran under suppressReconcile, and the cart:update it
      // dispatches carries source 'psn-cart-drawer', which onCartUpdate drops.
      // So nothing re-examines this tier until the customer's next mutation.
      // Belt and braces against a 200 that did not actually drop the line.
      const survived = (removeData.items || []).some(
        (item) => String(item.properties?._reward_gift_tier ?? '') === String(tierId)
      );
      if (survived) {
        rewards.state.suppressReconcile = false;
        await rewards.sync(removeData);
      }

      return true;
    } catch (error) {
      console.error('Reward gift decline failed:', error);
      this.#revertDecline(rewards, tierId);
      await this.applyCartAfterRewards();
      return false;
    } finally {
      this.state.isUpdating = false;
      rewards.state.suppressReconcile = false;
      await this.#drainDeclineQueue();
    }
  }

  // Run whatever the customer clicked while the last decline was in flight.
  // The key is re-read from the freshly morphed DOM, never reused, and a card
  // that has since gone (already removed, or the tier reset below its
  // threshold) is simply skipped.
  async #drainDeclineQueue() {
    if (this.state.decliningQueue) return;
    this.state.decliningQueue = true;

    try {
      while (this.state.declineQueue.length > 0) {
        const tierId = this.state.declineQueue.shift();
        const card = this.querySelector(`.psn-cart__reward-gift[data-reward-tier="${tierId}"]`);
        const key = card?.dataset.key;
        if (!key) continue;
        await this.declineRewardGift(key, tierId);
      }
    } finally {
      this.state.decliningQueue = false;
    }
  }

  #revertDecline(rewards, tierId) {
    rewards.state.pendingDeclines.delete(tierId);
    rewards.state.declinedTiers.delete(tierId);
  }

  // Same collapse the standard line uses, then drop the node. Leaving a class
  // on it instead would not survive: morph removes attributes the incoming
  // HTML does not carry.
  #collapseRewardGift(key) {
    const card = this.querySelector(`.psn-cart__reward-gift[data-key="${key}"]`);
    if (!card) return;

    if (this.#prefersReducedMotion()) {
      card.remove();
      return;
    }

    card.style.setProperty('--row-height', `${card.clientHeight}px`);
    card.classList.add('psn-cart-item--removing');
    this.#onAnimationEnd(card, () => card.remove());
  }

  // Cart JSON the section emits above its empty/non-empty branch, so it is
  // there even when the rewards block is not.
  #readEmbeddedCart() {
    const node = this.querySelector('[data-ref="cartState"]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  // An empty cart keeps its attributes, and CartRewards is not mounted on one,
  // so a refusal recorded earlier would outlive the basket it belonged to and
  // suppress the gift for the remaining life of the cart token. Shopify
  // deletes an attribute set to an empty string.
  async #clearDeclinedGiftsIfEmpty(cart) {
    if (!cart || (cart.item_count || 0) > 0) return;
    if (!cart.attributes?.[DECLINED_ATTRIBUTE]) return;

    try {
      await fetch(routes.cart_update_url, this.#fetchConfig('json', {
        body: JSON.stringify({ attributes: { [DECLINED_ATTRIBUTE]: '' } })
      }));
      cart.attributes[DECLINED_ATTRIBUTE] = '';
    } catch (error) {
      console.error('PSN Cart: clearing declined gifts failed:', error);
    }
  }

  #getExistingDiscounts() {
    const pills = this.querySelectorAll('.psn-cart__discount-pill[data-code]');
    return Array.from(pills).map(pill => pill.dataset.code).filter(Boolean);
  }

  #checkShippingDiscount(data, code, existingCodes) {
    const newHtml = data.sections?.[this.sectionId];
    if (!newHtml) return false;

    const fragment = new DOMParser().parseFromString(newHtml, 'text/html');
    const pills = fragment.querySelectorAll('.psn-cart__discount-pill[data-code]');
    const newCodes = Array.from(pills).map(pill => pill.dataset.code).filter(Boolean);

    return newCodes.length === existingCodes.length &&
           newCodes.every(c => existingCodes.includes(c)) &&
           data.discount_codes?.some(d => d.code === code && d.applicable);
  }

  #showDiscountError(message) {
    const error = this.refs.discountError;
    const errorText = this.refs.discountErrorText;
    
    if (error && errorText) {
      errorText.textContent = message;
      error.classList.remove('psn-cart__discount-error--hidden');
    }
  }

  #hideDiscountError() {
    const error = this.refs.discountError;
    if (error) {
      error.classList.add('psn-cart__discount-error--hidden');
    }
  }

  #fetchConfig(type = 'json', config = {}) {
    const headers = { 
      'Content-Type': 'application/json', 
      Accept: `application/${type}`, 
      ...config.headers 
    };

    if (type === 'javascript') {
      headers['X-Requested-With'] = 'XMLHttpRequest';
      delete headers['Content-Type'];
    }

    return {
      method: 'POST',
      headers,
      body: config.body,
    };
  }

  #debounce(fn, wait) {
    let timeout;
    
    function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    }

    debounced.cancel = () => clearTimeout(timeout);
    
    return debounced;
  }

  #prefersReducedMotion() {
    return PsnCartDrawer.#reducedMotion.matches;
  }

  #onAnimationEnd(elements, callback, options = { subtree: true }) {
    const animations = Array.isArray(elements)
      ? elements.flatMap((element) => element.getAnimations(options))
      : elements.getAnimations(options);
      
    const animationPromises = animations.reduce((acc, animation) => {
      if (animation.timeline instanceof DocumentTimeline) {
        acc.push(animation.finished);
      }
      return acc;
    }, []);

    return Promise.allSettled(animationPromises).then(callback);
  }

  initUpsell() {
    const container = this.refs.upsell;
    if (!container) return;

    const swiperEl = container.querySelector('.upsell-swiper');
    if (!swiperEl) return;

    // Remove slides for products already in cart
    const cartProductIds = new Set(
      Array.from(this.querySelectorAll('.psn-cart-item[data-product-id]'))
        .map(item => item.dataset.productId)
        .filter(Boolean)
    );
    cartProductIds.forEach(id => {
      container.querySelectorAll(`.upsell-product-${id}`).forEach(el => el.remove());
    });

    const slides = container.querySelectorAll('.swiper-slide-upsell');
    if (!slides.length) {
      swiperEl.style.display = 'none';
      return;
    }

    if (typeof Swiper !== 'undefined') {
      if (this._upsellSwiper) {
        try { this._upsellSwiper.destroy(true, true); } catch (_) {}
        this._upsellSwiper = null;
      }
      this._upsellSwiper = new Swiper(swiperEl, {
        spaceBetween: 10,
        slidesPerView: 1.2,
        watchSlidesProgress: true,
        scrollbar: {
          el: swiperEl.querySelector('.swiper-scrollbar'),
          draggable: true,
        }
      });
      container.classList.toggle('border', slides.length <= 1);
    }
  }

  initUpsellCardSelects() {
    this.querySelectorAll('[data-upsell-select]').forEach(select => {
      select.addEventListener('change', () => {
        const card = select.closest('.psn-upsell-card');
        if (!card) return;

        const selectedOption = select.options[select.selectedIndex];
        if (!selectedOption || selectedOption.disabled) return;

        // Update ADD button variant
        const addBtn = card.querySelector('.psn-upsell-card__add');
        if (addBtn) addBtn.dataset.variantId = select.value;

        // Update price display — prefer subscription price when present
        const priceEl = card.querySelector('[data-upsell-price]');
        if (priceEl) {
          const subPrice = selectedOption.dataset.subscriptionPrice;
          const otpPrice = selectedOption.dataset.price;
          if (subPrice) {
            priceEl.textContent = subPrice;
          } else if (otpPrice) {
            priceEl.textContent = otpPrice;
          }
        }

        // Update compare price
        const compareEl = card.querySelector('[data-upsell-compare]');
        if (selectedOption.dataset.hasCompare === 'true') {
          if (compareEl) {
            compareEl.textContent = selectedOption.dataset.compare;
            compareEl.style.display = '';
          } else if (priceEl) {
            const s = document.createElement('s');
            s.className = 'psn-upsell-card__compare';
            s.setAttribute('data-upsell-compare', '');
            s.textContent = selectedOption.dataset.compare;
            priceEl.parentNode.insertBefore(s, priceEl);
          }
        } else if (compareEl) {
          compareEl.style.display = 'none';
        }
      });
    });
  }

  initShippingProgress() {
    if (!this.refs.shipping) return;
    
    const shippingElement = this.refs.shipping;
    
    this.shippingConfig = {
      threshold: parseInt(shippingElement.dataset.shippingThreshold || 0, 10),
      shippingCost: parseInt(shippingElement.dataset.shippingCost || 0, 10),
      progressMessage: shippingElement.dataset.progressMessage || 'Spend <strong>{amount}</strong> more for free shipping',
      shippingLabel: shippingElement.dataset.shippingLabel || 'Shipping',
      shippingFreeText: shippingElement.dataset.shippingFreeText || 'FREE',
      moneyFormat: shippingElement.dataset.moneyFormat ? JSON.parse(shippingElement.dataset.moneyFormat) : null
    };

    if (this.shippingConfig.threshold <= 0) {
      console.warn('PSN Cart: Invalid shipping threshold configured');
      return;
    }

    const initialCartTotal = parseInt(shippingElement.dataset.cartTotal || 0, 10);
    const initialHasSubscription = shippingElement.dataset.hasSubscription === 'true';

    this.calculateShippingFromData(initialCartTotal, initialHasSubscription);
  }

  calculateShippingFromData(cartTotal, hasSubscription) {
    if (!this.refs.shipping || !this.shippingConfig) return;

  
    const threshold = this.shippingConfig.threshold;
    const isFreeShipping = hasSubscription || cartTotal >= threshold;
    const remainingAmount = Math.max(0, threshold - cartTotal);
    const progressPercent = isFreeShipping ? 100 : Math.min(100, (cartTotal / threshold) * 100);
  
    this.updateShippingUI(isFreeShipping, remainingAmount, progressPercent);
  }

  calculateShipping(cart) {
    if (!this.refs.shipping || !this.shippingConfig) return;

    const hasSubscription = cart.items?.some(item => item.selling_plan_allocation) || false;
    const cartTotal = cart.total_price || 0;

    
    this.calculateShippingFromData(cartTotal, hasSubscription);
  }

  updateShippingUI(isFreeShipping, remainingAmount, progressPercent) {
    if (!this.refs.shipping) return;
     
    if (isFreeShipping) {      
      this.refs.shippingSuccess?.classList.remove('psn-cart__shipping-success-wrapper--hidden');
      this.refs.shippingCost?.classList.add('psn-cart__shipping-cost--strikethrough');
      this.refs.shippingFree?.classList.add('psn-cart__shipping-free--show');
      this.refs.shippingMessageWrapper?.classList.add('psn-cart__shipping-message-wrapper--hidden');
    } else {
      this.refs.shippingSuccess?.classList.add('psn-cart__shipping-success-wrapper--hidden');
      this.refs.shippingMessageWrapper?.classList.remove('psn-cart__shipping-message-wrapper--hidden');
      this.refs.shippingCost?.classList.remove('psn-cart__shipping-cost--strikethrough');
      this.refs.shippingFree?.classList.remove('psn-cart__shipping-free--show');    
      
      if (this.refs.shippingMessage) {
        const formattedAmount = this.#formatMoney(remainingAmount);
        const message = this.shippingConfig.progressMessage.replace('{amount}', formattedAmount);
        this.refs.shippingMessage.innerHTML = message;
      }
    }


    if (this.refs.shippingFill) {
      this.refs.shippingFill.style.width = `${progressPercent}%`;
    }
  }

  #formatMoney(cents) {
    const format = this.shippingConfig?.moneyFormat;
    if (typeof window.formatMoney === 'function' && format) {
      return window.formatMoney(cents, format);
    }

    if (format) {
      const amount = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', amount);
    }

    const amount = (cents / 100).toFixed(2);
    return `£${amount}`;
  }

  // Reads the cart JSON the section embeds (see sections/psn-cart-drawer.liquid).
  // Parsed from the response string rather than the live DOM so it is available
  // before morph runs, and so it never depends on how morph treats script nodes.
  // Turn a mutation response into something applyCartAfterRewards can use.
  // /cart/change.js returns the whole cart, so it passes straight through.
  // /cart/add.js returns only the added lines, so the cart is read back out of
  // the rendered section, the same trick the add-to-cart path uses (PS001R-909).
  #cartFromMutationResponse(data) {
    const html = data?.sections?.[this.sectionId];
    if (!html) return null;
    if (typeof data.item_count === 'number') return data;

    const parsed = PsnCartDrawer.#parseCartState(html);
    return parsed ? { ...parsed, sections: data.sections } : null;
  }

  static #parseCartState(html) {
    try {
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const node = fragment.querySelector('[data-ref="cartState"]');
      return node ? JSON.parse(node.textContent) : null;
    } catch (error) {
      return null;
    }
  }

  #updateCartSection(html) {
    const sectionId = `${PsnCartDrawer.#SECTION_ID_PREFIX}${this.sectionId}`;

    const fragment = new DOMParser().parseFromString(html, 'text/html');
    const existingElement = document.getElementById(sectionId);
    const newElement = fragment.getElementById(sectionId);

    if (!existingElement || !newElement) return;

    morph(existingElement, newElement, MORPH_OPTIONS);
  }

  /* ============================================
     TIERED REWARDS SYSTEM — delegated to CartRewards (psn-cart-rewards.js)
     ============================================ */
  initRewards() {
    if (!this.refs.rewards) {
      this.rewards = null;
      return;
    }
    if (this.rewards) {
      // Re-attach to the new DOM root after morph — keeps runtime state
      // (tierLinesByTier, unavailableTiers, reconciling flag).
      this.rewards.rehydrate(this.refs.rewards);
    } else {
      this.rewards = new CartRewards(this.refs.rewards, { host: this });
      this.rewards.init();
    }
  }

  /* ============================================
     EMPTY CART ADD-TO-CART
     ============================================ */
  async addToCartFromRecommendation(variantId, button) {
    if (this.state.isUpdating) return;
    this.state.isUpdating = true;

    button.style.opacity = '0.4';
    button.style.pointerEvents = 'none';

    try {
      const sections = this.getAllCartSections();

      // Starter-kit upsell cards carry the kit's selling plan + source handle
      // (data-selling-plan-id / data-source) so the added line lands as a kit
      // subscription, matching the PDP Subscribe path. That makes the cart
      // drawer resolve kit_product via _source and render the inline size
      // selector on the line (Case B in psn-cart-drawer.liquid). Non-kit cards
      // omit both attributes and add a plain one-time line exactly as before.
      const sellingPlanId = button.dataset.sellingPlanId
        ? parseInt(button.dataset.sellingPlanId, 10)
        : null;
      const sourceHandle = button.dataset.source || null;

      const properties = { __upcartUpsell: variantId };
      if (sourceHandle) properties._source = sourceHandle;

      const item = {
        id: parseInt(variantId, 10),
        quantity: 1,
        properties
      };
      if (sellingPlanId) item.selling_plan = sellingPlanId;

      const body = JSON.stringify({
        items: [item],
        sections: sections.join(','),
        sections_url: window.location.pathname
      });

      const response = await fetch(routes.cart_add_url, this.#fetchConfig('json', { body }));
      const data = await response.json();

      if (!response.ok) {
        console.error('PSN Cart: Add to cart rejected:', data);
        return;
      }

      // /cart/add.js returns only the added items — not the full cart.
      // Fetch the full cart so rewards.sync() gets correct totals.
      const [freshCart, sectionsData] = await Promise.all([
        fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then(r => r.json()),
        data.sections
          ? Promise.resolve({ sections: data.sections })
          : fetch(routes.cart_update_url, this.#fetchConfig('json', {
              body: JSON.stringify({ sections: sections.join(','), sections_url: window.location.pathname })
            })).then(r => r.json())
      ]);
      freshCart.sections = sectionsData.sections;
      await this.refreshCart(freshCart);
    } catch (error) {
      console.error('PSN Cart: Add to cart failed:', error);
    } finally {
      this.state.isUpdating = false;
      button.style.opacity = '';
      button.style.pointerEvents = '';
    }
  }
}

  customElements.define('psn-cart-drawer', PsnCartDrawer);
}
